import React, { useEffect, useRef, useId } from 'react';
import { useModalFocusTrap } from '../../hooks/useModalFocusTrap';
import { acquireBodyScrollLock } from '../../utils/bodyScrollLock';
import { pushModalEscapeHandler } from '../../utils/modalEscapeStack';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl';
type ModalVariant = 'dialog' | 'sheet';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  size?: ModalSize;
  variant?: ModalVariant;
  showClose?: boolean;
  className?: string;
  /** Optional inline styles on the dialog panel (e.g. accent border). */
  style?: React.CSSProperties;
  footer?: React.ReactNode;
  closeOnOverlayClick?: boolean;
  /** When false, Escape does not close (e.g. while saving). Default true. */
  closeOnEscape?: boolean;
  /**
   * Where to place focus when the dialog opens.
   * `dialog` avoids jumping into the first deep form field (important for long Manage build).
   */
  initialFocus?: 'field' | 'dialog';
  /** Let long editors use the full phone viewport and one vertical scrollport. */
  mobileFullscreen?: boolean;
}

const dialogSizeClasses: Record<ModalSize, string> = {
  sm: 'modal-panel-sm',
  md: 'modal-panel-md',
  lg: 'modal-panel-lg p-6',
  xl: 'modal-panel max-w-4xl max-h-[90vh] overflow-y-auto p-6',
};

const sheetSizeClasses: Record<ModalSize, string> = {
  sm: 'modal-sheet w-full sm:max-w-sm p-6',
  md: 'modal-sheet w-full sm:max-w-lg p-6',
  lg: 'modal-sheet w-full sm:max-w-2xl p-6',
  xl: 'modal-sheet w-full sm:max-w-4xl p-6',
};

const FIELD_FOCUS_SELECTOR =
  'input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled])';
const ANY_FOCUSABLE_SELECTOR =
  'input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  size = 'md',
  variant = 'dialog',
  showClose = true,
  className = '',
  style,
  footer,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  initialFocus = 'field',
  mobileFullscreen = false,
}) => {
  const titleId = useId();
  const descriptionId = useId();
  const overlayRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const closeOnEscapeRef = useRef(closeOnEscape);
  closeOnEscapeRef.current = closeOnEscape;

  // Focus trap still applies; initial focus target is controlled separately below.
  useModalFocusTrap(isOpen, panelRef, {
    returnFocus: true,
    skipInitialFocus: true,
  });

  // Escape stack + body scroll lock — only the topmost open modal closes on Escape.
  useEffect(() => {
    if (!isOpen) return undefined;

    const unregister = pushModalEscapeHandler(() => {
      if (closeOnEscapeRef.current) onCloseRef.current();
    });
    const releaseScrollLock = acquireBodyScrollLock();

    return () => {
      unregister();
      releaseScrollLock();
    };
  }, [isOpen]);

  // The visible viewport shrinks and moves when the phone keyboard opens.
  useEffect(() => {
    if (!isOpen || !mobileFullscreen) return;
    const viewport = window.visualViewport;
    const update = () => {
      overlayRef.current?.style.setProperty('--editor-height', `${viewport?.height ?? window.innerHeight}px`);
      overlayRef.current?.style.setProperty('--editor-top', `${viewport?.offsetTop ?? 0}px`);
    };
    update();
    viewport?.addEventListener('resize', update);
    viewport?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      viewport?.removeEventListener('resize', update);
      viewport?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [isOpen, mobileFullscreen]);

  // Initial focus once when the modal opens.
  useEffect(() => {
    if (!isOpen) return undefined;

    const timer = window.setTimeout(() => {
      const panel = panelRef.current;
      if (!panel) return;

      if (initialFocus === 'dialog') {
        if (!panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '-1');
        panel.focus({ preventScroll: true });
        return;
      }

      const firstField = panel.querySelector<HTMLElement>(FIELD_FOCUS_SELECTOR);
      const target = firstField ?? panel.querySelector<HTMLElement>(ANY_FOCUSABLE_SELECTOR);

      target?.focus({ preventScroll: true });
    }, 50);

    return () => {
      window.clearTimeout(timer);
    };
  }, [isOpen, initialFocus]);

  if (!isOpen) return null;

  const panelClasses = variant === 'sheet' ? sheetSizeClasses[size] : dialogSizeClasses[size];

  return (
    <div
      className={`${variant === 'sheet' ? 'modal-overlay-sheet' : 'modal-overlay'} ${mobileFullscreen ? 'modal-overlay-mobile-fullscreen' : ''}`}
      ref={overlayRef}
      role="presentation"
      onClick={(e) => {
        if (closeOnOverlayClick && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descriptionId : undefined}
        className={`${panelClasses} ${footer ? 'flex flex-col max-h-[min(92vh,100%)] overflow-hidden' : ''} ${mobileFullscreen ? 'modal-mobile-fullscreen' : ''} ${className}`}
        style={style}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || showClose) && (
          <div className="flex items-start justify-between gap-4 mb-4 sm:mb-5 shrink-0">
            <div className="min-w-0">
              {title && (
                <h2 id={titleId} className="modal-panel-title text-xl">
                  {title}
                </h2>
              )}
              {description && (
                <p id={descriptionId} className="modal-panel-description">
                  {description}
                </p>
              )}
            </div>
            {showClose && (
              <button
                type="button"
                onClick={onClose}
                className="btn-icon shrink-0 -mr-1 -mt-1"
                aria-label="Close dialog"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        )}
        {/* Scroll the body only — never sticky-foot over content (transform animations
            on .modal-panel break position:sticky and caused overlapping action rows). */}
        <div
          className={`min-h-0 min-w-0 flex flex-col ${footer ? 'flex-1 overflow-y-auto overscroll-contain' : mobileFullscreen ? 'flex-1 overflow-hidden' : ''}`}
        >
          {children}
        </div>
        {footer != null && (
          <div className="shrink-0 mt-3 pt-3 sm:mt-5 sm:pt-4 border-t border-white/[0.06] bg-[#0c0c0c]">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
